import type { Driver } from 'driver.js'
import { Button, Icon } from '@/components/atoms'
import { createRoot } from 'react-dom/client'
import { getOnExplicitDismissCallback } from '@/hooks/useAppWalkthrough/useAppWalkthroughDriverConfig'

interface RenderPopoverFooterOptions {
    popover: {
        previousButton: HTMLElement
        nextButton: HTMLElement | null
        closeButton: HTMLElement
        footer?: HTMLElement
    }
    opts: {
        config: {
            showButtons?: string[]
            nextBtnText?: string
            prevBtnText?: string
            closeBtnText?: string
        }
        state: {
            activeStep?: {
                popover?: {
                    showButtons?: string[]
                    nextBtnText?: string
                }
            }
        }
        driver: Driver
    }
    config: ReturnType<Driver['getConfig']> & {
        closeBtnText?: string
    }
}

const createButton = (
    label: string,
    variant: 'solidPrimary' | 'outlineSecondary' | 'ghostSecondary',
    onClick: () => void,
    showChevron?: boolean
): HTMLDivElement => {
    const wrapper = document.createElement('div')
    const root = createRoot(wrapper)
    root.render(
        <Button name={''} variant={variant} size="large" onClick={onClick}>
            {label}
            {showChevron && <Icon name="chevron_right" size="small" />}
        </Button>
    )
    return wrapper
}

export const renderPopoverFooter = (
    options: RenderPopoverFooterOptions
): void => {
    const { popover, opts, config } = options

    const stepButtons =
        opts.state.activeStep?.popover?.showButtons || opts.config.showButtons
    const hasPrevious = stepButtons?.includes('previous')
    const hasClose = stepButtons?.includes('close')
    const hasNext = stepButtons?.includes('next')

    if (hasPrevious) {
        const previousButtonLabel = config.prevBtnText || 'Previous'
        const previousButton = createButton(
            previousButtonLabel,
            'outlineSecondary',
            opts.driver.movePrevious
        )
        popover.previousButton.replaceWith(previousButton)
    }

    const footerContainer = document.createElement('div')
    footerContainer.style.display = 'flex'
    footerContainer.style.gap = '8px'
    footerContainer.style.justifyContent = 'flex-end'

    if (hasClose) {
        const closeButtonLabel = config.closeBtnText || 'Dismiss'
        const dismissButton = createButton(
            closeButtonLabel,
            'ghostSecondary',
            () => {
                const dismissCallback = getOnExplicitDismissCallback()
                if (dismissCallback) {
                    dismissCallback()
                } else {
                    opts.driver.destroy()
                }
            }
        )
        footerContainer.appendChild(dismissButton)
    }

    if (hasNext) {
        const nextButtonLabel =
            opts.state.activeStep?.popover?.nextBtnText ||
            config.nextBtnText ||
            'Next'
        const doneButtonLabel = config.doneBtnText || 'Done'
        const shouldShowChevron = nextButtonLabel !== doneButtonLabel
        const nextButton = createButton(
            nextButtonLabel,
            'solidPrimary',
            opts.driver.moveNext,
            shouldShowChevron
        )
        footerContainer.appendChild(nextButton)
    }

    if (footerContainer.children.length > 0) {
        if (popover.nextButton !== null) {
            popover.nextButton.replaceWith(footerContainer)
        } else {
            const buttonContainer =
                popover.footer || popover.closeButton.parentElement
            if (buttonContainer !== null) {
                buttonContainer.appendChild(footerContainer)
            }
        }
    }
}
